import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  X,
  ChevronLeft,
  LayoutDashboard,
  Activity,
  HeartPulse,
  Link2,
  History,
  Terminal,
  Loader2,
  WifiOff,
  PackageCheck,
  Watch,
  SquareTerminal,
  HardDrive,
  BatteryCharging,
} from "lucide-react";
import Modal from "components/common/Modal";
import {
  useCaseList,
  useCaseConnection,
  useCaseCommand,
} from "hooks/useCaseDashboard";
import CasesView, { errorText } from "./CasesView";
import OverviewView from "./OverviewView";
import LiveSessionView from "./LiveSessionView";
import HealthView from "./HealthView";
import FleetView from "./FleetView";
import PairingView from "./PairingView";
import HistoryView from "./HistoryView";
import LogsView from "./LogsView";
import SoftwareView from "./SoftwareView";
import TerminalView from "./TerminalView";
import StorageView from "./StorageView";
import BatteryView from "./BatteryView";
import { EmptyState, PrimaryButton, StatusDot } from "./parts";

const TABS = [
  { id: "overview", icon: LayoutDashboard },
  { id: "live", icon: Activity },
  { id: "fleet", icon: Watch },
  { id: "battery", icon: BatteryCharging },
  { id: "health", icon: HeartPulse },
  { id: "pairing", icon: Link2 },
  { id: "history", icon: History },
  { id: "storage", icon: HardDrive },
  { id: "software", icon: PackageCheck },
  { id: "logs", icon: Terminal },
  { id: "terminal", icon: SquareTerminal },
];

const CONNECTION_TONE = {
  connected: "ok",
  connecting: "warn",
  reconnecting: "warn",
  disconnected: "never",
  error: "bad",
};

/** Everything for one connected case: sidebar tabs + the selected view. */
const CaseWorkspace = ({ caseRecord, connection }) => {
  const { t } = useTranslation();
  const [tab, setTab] = useState("overview");
  const [replayId, setReplayId] = useState(null);
  const connected = connection.state === "connected";
  const caseId = caseRecord.id;

  // Shared by the header, the overview and the live view.
  const status = useCaseCommand(caseId, "status", 15000, connected);
  const health = useCaseCommand(caseId, "health", 30000, connected);
  const refreshAll = useCallback(() => {
    status.refresh();
    health.refresh();
  }, [status, health]);

  if (!connected) {
    const failed = connection.state === "error";
    return (
      <div className="flex flex-1 items-center justify-center">
        <EmptyState
          icon={failed ? WifiOff : Loader2}
          title={t(`caseDashboard.connection.${connection.state}`)}
          body={failed ? errorText(t, connection.error) : null}
          action={
            failed && (
              <PrimaryButton onClick={connection.retry}>
                {t("caseDashboard.common.retry")}
              </PrimaryButton>
            )
          }
        />
      </div>
    );
  }

  const body = () => {
    switch (tab) {
      case "live":
        return <LiveSessionView caseId={caseId} mode={status.data?.mode} />;
      case "health":
        return <HealthView caseId={caseId} health={health} />;
      case "fleet":
        return <FleetView caseId={caseId} />;
      case "battery":
        return <BatteryView caseId={caseId} />;
      case "pairing":
        return <PairingView caseId={caseId} />;
      case "history":
        return replayId ? (
          <LiveSessionView
            caseId={caseId}
            replaySessionId={replayId}
            onBack={() => setReplayId(null)}
          />
        ) : (
          <HistoryView caseId={caseId} onOpen={setReplayId} />
        );
      case "storage":
        return <StorageView caseId={caseId} />;
      case "software":
        return (
          <SoftwareView
            caseId={caseId}
            caseRecord={caseRecord}
            status={status}
            health={health}
          />
        );
      case "logs":
        return <LogsView caseId={caseId} />;
      case "terminal":
        return <TerminalView caseId={caseId} caseRecord={caseRecord} />;
      default:
        return (
          <OverviewView
            caseId={caseId}
            caseRecord={caseRecord}
            status={status}
            health={health}
            onChanged={refreshAll}
            onNavigate={setTab}
          />
        );
    }
  };

  return (
    <div className="flex min-h-0 flex-1">
      <nav className="w-56 shrink-0 border-r border-[#F3F4F6] bg-white p-3">
        {TABS.map(({ id, icon: Icon }) => (
          <button
            type="button"
            key={id}
            onClick={() => {
              setTab(id);
              if (id !== "history") setReplayId(null);
            }}
            className={`mb-1 flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm transition duration-150 ${
              tab === id
                ? "bg-[#FEF3E7] font-semibold text-[#C2611F]"
                : "font-medium text-[#374151] hover:bg-[#F9FAFB]"
            }`}
          >
            <Icon
              size={16}
              className={tab === id ? "text-[#E07A3A]" : "text-[#9CA3AF]"}
            />
            {t(`caseDashboard.tabs.${id}`)}
          </button>
        ))}
      </nav>
      <main className="min-w-0 flex-1 overflow-y-auto bg-[#F8F9FA] p-6">
        {body()}
      </main>
    </div>
  );
};

const CaseDashboardModal = ({ show, onClose }) => {
  const { t } = useTranslation();
  const list = useCaseList();
  const [caseId, setCaseId] = useState(null);
  const connection = useCaseConnection(show ? caseId : null);
  const caseRecord = list.cases.find((c) => c.id === caseId) || null;

  // Refresh the saved list each time the dashboard opens.
  const { reload } = list;
  useEffect(() => {
    if (show) reload();
  }, [show, reload]);

  const close = () => {
    setCaseId(null);
    onClose();
  };

  return (
    <Modal show={show} onClose={close} fullScreen>
      <div
        className="flex shrink-0 items-center justify-between px-6 py-4"
        style={{
          background: "linear-gradient(135deg, #E8854A 0%, #D66B2E 100%)",
        }}
      >
        <div className="flex min-w-0 items-center gap-3">
          {caseRecord && (
            <button
              type="button"
              onClick={() => setCaseId(null)}
              className="flex items-center gap-1 rounded-lg px-2 py-1 text-sm font-medium text-white/90 transition duration-150 hover:bg-white/10 hover:text-white"
            >
              <ChevronLeft size={16} />
              {t("caseDashboard.tabs.back")}
            </button>
          )}
          <h2 className="truncate text-base font-semibold text-white">
            {caseRecord ? caseRecord.name : t("caseDashboard.title")}
          </h2>
          {caseRecord && (
            <span className="flex items-center gap-1.5 rounded bg-white/15 px-2 py-0.5 text-[11px] font-medium text-white">
              <StatusDot
                tone={CONNECTION_TONE[connection.state] || "info"}
                pulse={connection.state === "connected"}
                size={6}
              />
              {t(`caseDashboard.connection.${connection.state}`)}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={close}
          aria-label={t("caseDashboard.close")}
          className="flex h-8 w-8 items-center justify-center rounded-lg text-white/80 transition duration-150 hover:bg-white/10 hover:text-white"
        >
          <X size={18} />
        </button>
      </div>

      {caseRecord ? (
        <CaseWorkspace
          key={caseRecord.id}
          caseRecord={caseRecord}
          connection={connection}
        />
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto bg-[#F8F9FA] p-6">
          <CasesView list={list} onOpen={setCaseId} />
        </div>
      )}
    </Modal>
  );
};

export default CaseDashboardModal;
